// Campaigns tab status buckets (EPD-12315, EPD-12316, EPD-5559, EPD-12321, EPD-12476).

import type {
  CreatorInteraction,
  PendingApplication,
  Project,
} from "@/lib/queries/projects";
import { compareIsoAsc } from "./dates";

export const CAMPAIGN_STATUSES = [
  "applied",
  "in-progress",
  "completed",
  "archived",
] as const;

export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  applied: "Applied",
  "in-progress": "In Progress",
  completed: "Completed",
  archived: "Archived",
};

/** Task statuses that leave nothing for the creator to do. */
const DONE_STATUSES = new Set([
  "COMPLETED",
  "DISABLED",
  "DEACTIVATED",
  "AVAILABLE",
]);

/** One card or row on the Campaigns tab: a campaign, or a pending application. */
export interface CampaignItem {
  key: string;
  status: CampaignStatus;
  project: Project;
  /** In Progress: the first task waiting on the creator, shown as the badge. */
  nextTask: CreatorInteraction | null;
  /** Task types waiting on the creator, for the task filter. */
  actionTaskTypes: string[];
  /** Latest task update (ISO); for an archived campaign, when it was archived. */
  updatedDate: string | null;
  /**
   * In Progress because the brand added a task after the creator archived it
   * (EPD-12321). Shows the "New task" badge until the creator opens it.
   */
  newTask: boolean;
  /** Applied: when the creator applied (ISO). */
  appliedDate: string | null;
  /**
   * When the creator was added (ISO), for the brand card image (EPD-12612):
   * the applied date, or the creator's earliest task. Null when unknown.
   */
  addedDate: string | null;
  /** Applied: the brand's support email from the application page. */
  supportEmail: string | null;
}

/**
 * Which tab a joined campaign belongs to, or null when it isn't shown. A legacy
 * (non-V2) campaign with no tasks means the creator hasn't been fully added yet,
 * so it stays hidden, as it was before. Interactions that failed to load leave
 * the campaign visible under In Progress.
 *
 * EPD-12476: a campaign the brand archived is placed by its tasks like any
 * other. Brands often archive before the work is done (to stay under their
 * active-campaign limit), and the creator may still have deliverables or a
 * payment coming.
 */
export function projectStatus(
  project: Project,
  interactions: CreatorInteraction[] | undefined,
): "in-progress" | "completed" | null {
  if (interactions && interactions.length === 0 && !project.isProjectV2) {
    return null;
  }
  if (
    interactions &&
    interactions.length > 0 &&
    interactions.every((interaction) => DONE_STATUSES.has(interaction.status))
  ) {
    return "completed";
  }
  return "in-progress";
}

/**
 * EPD-12321: a task waiting on the creator that wasn't open when they archived,
 * i.e. something new from the brand. Cancellations and deactivations don't
 * count, so a cancelled campaign stays archived.
 */
export const hasNewTask = (
  archive: NonNullable<Project["creatorArchive"]>,
  interactions: CreatorInteraction[] | undefined,
) =>
  (interactions ?? []).some(
    (interaction) =>
      interaction.status === "ACTION_REQUIRED" &&
      !archive.openTaskIds.includes(interaction.id),
  );

/**
 * Where the creator's own archive puts a campaign: "archived", "returned" when
 * a new task brought it back (saved, or just seen), or null when they never
 * archived it.
 */
export function archiveState(
  project: Project,
  interactions: CreatorInteraction[] | undefined,
): "archived" | "returned" | null {
  const archive = project.creatorArchive;
  if (!archive) return null;
  if (archive.returnedAt || hasNewTask(archive, interactions)) {
    return "returned";
  }
  return "archived";
}

const latestUpdate = (interactions: CreatorInteraction[]): string | null =>
  interactions.reduce<string | null>(
    (latest, interaction) =>
      interaction.updatedDate && (!latest || interaction.updatedDate > latest)
        ? interaction.updatedDate
        : latest,
    null,
  );

const earliestCreated = (interactions: CreatorInteraction[]): string | null =>
  interactions.reduce<string | null>(
    (earliest, interaction) =>
      compareIsoAsc(interaction.createdDate, earliest) < 0
        ? (interaction.createdDate ?? null)
        : earliest,
    null,
  );

export const interactionKey = (project: { profileId: string; id: number }) =>
  `${project.profileId}-${project.id}`;

/**
 * Every campaign and pending application as Campaigns tab items. Campaigns come
 * newest activity first and applications in API order (newest first), which is
 * the default order before a column is sorted.
 */
export function buildCampaignItems(
  projects: Project[],
  interactionMap: ReadonlyMap<string, CreatorInteraction[]>,
  applications: PendingApplication[],
): CampaignItem[] {
  const campaigns = projects.flatMap((project): CampaignItem[] => {
    const interactions = interactionMap.get(interactionKey(project));
    const taskStatus = projectStatus(project, interactions);
    if (!taskStatus) return [];
    const archive = archiveState(project, interactions);
    const status = archive === "archived" ? "archived" : taskStatus;
    const actionTasks = (interactions ?? []).filter(
      (interaction) => interaction.status === "ACTION_REQUIRED",
    );
    return [
      {
        key: `campaign-${interactionKey(project)}`,
        status,
        project,
        nextTask: status === "in-progress" ? (actionTasks[0] ?? null) : null,
        actionTaskTypes: actionTasks.flatMap((task) =>
          task.taskType ? [task.taskType] : [],
        ),
        updatedDate:
          status === "archived"
            ? (project.creatorArchive?.archivedAt ?? null)
            : latestUpdate(interactions ?? []),
        newTask: archive === "returned" && status === "in-progress",
        appliedDate: null,
        addedDate: earliestCreated(interactions ?? []),
        supportEmail: null,
      },
    ];
  });
  campaigns.sort((a, b) =>
    (b.updatedDate ?? "").localeCompare(a.updatedDate ?? ""),
  );

  const applied = applications.map(
    (application): CampaignItem => ({
      key: `application-${interactionKey(application)}`,
      status: "applied",
      project: application,
      nextTask: null,
      actionTaskTypes: [],
      updatedDate: null,
      newTask: false,
      appliedDate: application.appliedDate,
      addedDate: application.appliedDate,
      supportEmail: application.supportEmail,
    }),
  );

  return [...applied, ...campaigns];
}

/** The status badge on a card or row, or null when there is nothing to show. */
export function campaignBadge(item: CampaignItem): {
  label: string;
  variant: "outline" | "warning" | "success";
} | null {
  switch (item.status) {
    case "applied":
      return { label: "Application pending", variant: "outline" };
    case "in-progress":
      if (item.newTask) return { label: "New task", variant: "warning" };
      return item.nextTask
        ? { label: item.nextTask.title, variant: "warning" }
        : null;
    case "completed":
      return { label: "Completed", variant: "success" };
    case "archived":
      return { label: "Archived", variant: "outline" };
  }
}
